import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { cn, formatMoney, formatPercent } from '@/lib/utils'
import { needByLabel } from '@/features/storefront-chat/format'
import type {
  ChatCartFacts,
  ChatContext,
  ChatCustomerFacts,
  ChatProductFacts,
} from '@/features/storefront-chat/types'

const LEAD_TIME_SOURCE: Record<ChatProductFacts['lead_time_source'], string> = {
  product: 'product',
  manufacturer: 'manufacturer',
  default: 'store default',
}

/** Label-left, value-right rows; the rail is too narrow for a grid of cells. */
function Facts({ rows, className }: { rows: [string, ReactNode][]; className?: string }) {
  if (rows.length === 0) return null
  return (
    <dl className={cn('space-y-1 text-xs', className)}>
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-baseline justify-between gap-3">
          <dt className="shrink-0 text-muted-foreground">{label}</dt>
          <dd className="min-w-0 text-right font-medium tabular-nums text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function leadTime(product: ChatProductFacts) {
  if (product.lead_time_days == null) return 'Store default'
  const days = `${product.lead_time_days} day${product.lead_time_days === 1 ? '' : 's'}`
  return `${days} · ${LEAD_TIME_SOURCE[product.lead_time_source]}`
}

/** Our pricing and the logistics a person weighs a discount or a faster ship date against. */
export function ProductFacts({ product }: { product: ChatProductFacts }) {
  const size = [product.dimensions, product.shipping_weight_lbs != null ? `${product.shipping_weight_lbs} lb` : null]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className="space-y-2.5">
      <Facts
        rows={[
          ['Price', <span className="text-sm font-semibold">{formatMoney(product.price)}</span>],
          ['Net', formatMoney(product.net)],
          [
            'Profit',
            <span className="text-green-700 dark:text-green-500">
              {formatMoney(product.profit)}
              {product.margin != null && ` · ${formatPercent(product.margin)}`}
            </span>,
          ],
          ...(product.markup != null ? [['Markup', formatPercent(product.markup)] as [string, ReactNode]] : []),
          ...(product.map_price != null ? [['MAP', formatMoney(product.map_price)] as [string, ReactNode]] : []),
          ...(product.list_price != null ? [['List', formatMoney(product.list_price)] as [string, ReactNode]] : []),
        ]}
      />
      <Facts
        className="border-t pt-2.5"
        rows={[
          ['Lead time', leadTime(product)],
          ['Ships in', product.ships_in],
          ...(product.free_shipping ? [['Shipping', 'Free'] as [string, ReactNode]] : []),
          ...(size ? [['Size', size] as [string, ReactNode]] : []),
          ...(product.warranty ? [['Warranty', product.warranty] as [string, ReactNode]] : []),
        ]}
      />
      <Link
        to="/products/$productId"
        params={{ productId: product.product_uuid }}
        className="block border-t pt-2 text-xs font-medium text-usa-blue underline-offset-2 hover:underline"
      >
        Product details
      </Link>
    </div>
  )
}

function CartFacts({ cart }: { cart: ChatCartFacts }) {
  return (
    <div className="space-y-2.5">
      <ul className="space-y-2">
        {cart.items.map((line, i) => (
          <li key={`${line.handle ?? line.title}-${i}`} className="text-xs">
            <div className="flex items-baseline justify-between gap-3">
              <span className="line-clamp-2 min-w-0 text-foreground">
                <span className="font-medium tabular-nums">{line.quantity} × </span>
                {line.product_uuid ? (
                  <Link
                    to="/products/$productId"
                    params={{ productId: line.product_uuid }}
                    className="underline-offset-2 hover:underline"
                  >
                    {line.title}
                  </Link>
                ) : (
                  line.title
                )}
              </span>
              <span className="shrink-0 font-medium tabular-nums text-foreground">{formatMoney(line.price)}</span>
            </div>
            {line.net != null && (
              <p className="mt-0.5 text-muted-foreground tabular-nums">
                Net {formatMoney(line.net)} · Profit {formatMoney(line.profit)}
                {line.margin != null && ` · ${formatPercent(line.margin)}`}
              </p>
            )}
          </li>
        ))}
      </ul>
      <Facts
        className="border-t pt-2.5"
        rows={[
          ['Subtotal', <span className="text-sm font-semibold">{formatMoney(cart.subtotal)}</span>],
          ...(Number(cart.discount) > 0 ? [['Discount', formatMoney(cart.discount)] as [string, ReactNode]] : []),
          ['Net', formatMoney(cart.net)],
          [
            'Profit',
            <span className="text-green-700 dark:text-green-500">
              {formatMoney(cart.profit)}
              {cart.margin != null && ` · ${formatPercent(cart.margin)}`}
            </span>,
          ],
        ]}
      />
    </div>
  )
}

function customerRows(customer: ChatCustomerFacts): [string, ReactNode][] {
  const rows: [string, ReactNode][] = []
  if (customer.orders_count != null) rows.push(['Orders', customer.orders_count])
  if (customer.lifetime_spend != null && Number(customer.lifetime_spend) > 0)
    rows.push(['Spent', formatMoney(customer.lifetime_spend)])
  if (customer.company) rows.push(['Company', customer.company])
  if (customer.location) rows.push(['Location', customer.location])
  if (customer.phone) rows.push(['Phone', customer.phone])
  if (customer.shipping_zip) rows.push(['Ships to', customer.shipping_zip])
  if (customer.tax_exempt) rows.push(['Tax', 'Exempt'])
  return rows
}

/** One closed line with the figure that matters most; opens to the rest. */
export function Fold({ label, summary, children }: { label: string; summary: ReactNode; children: ReactNode }) {
  return (
    <details className="group rounded-md border bg-background">
      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-md px-2.5 py-2 text-xs hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
        <span className="shrink-0 font-medium text-foreground">{label}</span>
        <span className="min-w-0 flex-1 truncate text-right tabular-nums text-muted-foreground">{summary}</span>
        <ChevronDown aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t px-2.5 py-2.5">{children}</div>
    </details>
  )
}

/** When the shopper needs their order, with their own words under it. */
function NeedBy({ date, words }: { date: string; words?: string | null }) {
  return (
    <div className="text-xs">
      <p className="text-muted-foreground">Needs it by</p>
      <p className="font-medium text-foreground">{needByLabel(date)}</p>
      {words && <p className="mt-0.5 break-words text-muted-foreground">“{words}”</p>}
    </div>
  )
}

function customerSummary(customer: ChatCustomerFacts) {
  if (customer.need_by) return `By ${needByLabel(customer.need_by, { weekday: false })}`
  if (customer.company) return customer.company
  if (customer.orders_count != null) return `${customer.orders_count} order${customer.orders_count === 1 ? '' : 's'}`
  if (customer.location) return customer.location
  return customer.shipping_zip ? `Ships to ${customer.shipping_zip}` : null
}

/** What a person needs to take the chat over, each folded to one line: our pricing on the viewed
 * product, the cart, the customer. */
export function StaffContextSections({ context, children }: { context: ChatContext | undefined; children?: ReactNode }) {
  const viewing = context?.products.find((product) => product.handle === context.viewing_handle)
  const customer = context ? customerRows(context.customer) : []
  const items = context?.cart?.quantity ?? 0
  return (
    <div className="space-y-1.5">
      {viewing && (
        <Fold
          label="Pricing"
          summary={`Net ${formatMoney(viewing.net)}${viewing.margin != null ? ` · ${formatPercent(viewing.margin)}` : ''}`}
        >
          <ProductFacts product={viewing} />
        </Fold>
      )}
      {context?.cart && (
        <Fold label="Cart" summary={`${items} item${items === 1 ? '' : 's'} · ${formatMoney(context.cart.subtotal)}`}>
          <CartFacts cart={context.cart} />
        </Fold>
      )}
      {context && (customer.length > 0 || context.customer.need_by) && (
        <Fold label="Customer" summary={customerSummary(context.customer)}>
          {context.customer.need_by && <NeedBy date={context.customer.need_by} words={context.customer.need_by_words} />}
          <Facts rows={customer} className={cn(context.customer.need_by && 'mt-2')} />
        </Fold>
      )}
      {children}
    </div>
  )
}
